/**
 * Session Security Manager
 * Handles session timeout, inactivity monitoring, and session hijacking prevention
 * HIPAA Compliant - Ensures secure session management
 */

class SessionSecurityManager {
  constructor() {
    this.inactivityTimer = null;
    this.sessionCheckInterval = null;
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    this.SESSION_CHECK_INTERVAL = 30 * 1000; // 30 seconds
    this.sessionId = null;
    this.lastActivity = Date.now();
    this.isLocked = false;
  }

  /**
   * Initialize session security
   */
  initialize(config = {}) {
    this.INACTIVITY_TIMEOUT = config.inactivityTimeout || this.INACTIVITY_TIMEOUT;
    this.sessionId = this.generateSessionId();
    
    // Store session info
    sessionStorage.setItem('session_id', this.sessionId);
    sessionStorage.setItem('session_start', Date.now());
    
    // Setup monitoring
    this.setupInactivityMonitoring();
    this.setupSessionValidation();
    this.setupBeforeUnloadHandler();
    this.setupStorageListener();
    
    console.info('[Session Security] ✅ Session security initialized');
    console.info(`[Session Security] Auto-lock after ${this.INACTIVITY_TIMEOUT / 60000} minutes`);
  }

  /**
   * Setup inactivity monitoring
   */
  setupInactivityMonitoring() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    const resetTimer = () => {
      if (this.isLocked) return;
      
      this.lastActivity = Date.now();
      clearTimeout(this.inactivityTimer);
      
      this.inactivityTimer = setTimeout(() => {
        this.handleInactivity();
      }, this.INACTIVITY_TIMEOUT);
    };

    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Initial timer
    resetTimer();
  }

  /**
   * Handle inactivity timeout
   */
  handleInactivity() {
    console.warn('[Session Security] Session locked due to inactivity');
    this.isLocked = true;
    
    // Log security event
    this.logSecurityEvent('session_locked_inactivity');
    
    // Show lock screen
    this.showLockScreen();
  }

  /**
   * Show lock screen
   */
  showLockScreen() {
    const lockScreen = document.createElement('div');
    lockScreen.id = 'session-lock-screen';
    lockScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      z-index: 10000000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease-out;
    `;

    const sessionDuration = Math.floor((Date.now() - sessionStorage.getItem('session_start')) / 60000);

    lockScreen.innerHTML = `
      <div style="
        background: white;
        padding: 50px;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        text-align: center;
        max-width: 500px;
      ">
        <div style="font-size: 72px; margin-bottom: 20px;">🔒</div>
        <h2 style="color: #333; margin: 0 0 15px 0;">Session Locked</h2>
        <p style="color: #666; margin-bottom: 10px;">
          Your session has been locked due to inactivity for security reasons.
        </p>
        <p style="color: #999; font-size: 14px; margin-bottom: 30px;">
          Session duration: ${sessionDuration} minutes<br>
          Session ID: ${this.sessionId.slice(0, 12)}...
        </p>
        
        <button onclick="window.location.href='/login'" style="
          width: 100%;
          padding: 15px;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        " onmouseover="this.style.background='#5568d3'" 
           onmouseout="this.style.background='#667eea'">
          Re-authenticate to Continue
        </button>
        
        <p style="color: #999; font-size: 12px; margin-top: 20px;">
          For your security, please log in again to access patient data.
        </p>
      </div>
    `;

    document.body.appendChild(lockScreen);
  }

  /**
   * Setup session validation (check for concurrent sessions)
   */
  setupSessionValidation() {
    this.sessionCheckInterval = setInterval(async () => {
      try {
        const response = await fetch('/api/auth/validate-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            sessionId: this.sessionId
          })
        });

        const data = await response.json();

        if (!data.valid) {
          this.handleInvalidSession(data.reason);
        }
      } catch (error) {
        console.error('[Session Security] Failed to validate session:', error);
      }
    }, this.SESSION_CHECK_INTERVAL);
  }

  /**
   * Handle invalid session
   */
  handleInvalidSession(reason) {
    console.error('[Session Security] Session invalidated:', reason);
    
    clearInterval(this.sessionCheckInterval);
    
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      z-index: 10000001;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    modal.innerHTML = `
      <div style="
        background: white;
        padding: 40px;
        border-radius: 12px;
        text-align: center;
        max-width: 500px;
      ">
        <div style="font-size: 64px; margin-bottom: 20px;">⚠️</div>
        <h2 style="color: #dc3545; margin: 0 0 15px 0;">Session Terminated</h2>
        <p style="color: #666; margin-bottom: 20px;">
          ${reason === 'concurrent_login' 
            ? 'Your account has been accessed from another location.' 
            : 'Your session has expired or been terminated.'}
        </p>
        <button onclick="window.location.href='/login'" style="
          padding: 15px 40px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
        ">Return to Login</button>
      </div>
    `;

    document.body.appendChild(modal);
    
    // Clear session data
    setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }, 3000);
  }

  /**
   * Setup before unload handler
   */
  setupBeforeUnloadHandler() {
    window.addEventListener('beforeunload', () => {
      this.logSecurityEvent('session_ended');
      
      // Clear sensitive data from memory
      this.clearSensitiveData();
    });
  }

  /**
   * Setup storage listener (detect tampering)
   */
  setupStorageListener() {
    window.addEventListener('storage', (e) => {
      if (e.key === 'token' && e.oldValue && !e.newValue) {
        // Token was removed - possible tampering
        this.logSecurityEvent('token_removed_suspicious');
        window.location.href = '/login';
      }
    });
  }

  /**
   * Clear sensitive data
   */
  clearSensitiveData() {
    // Clear any sensitive data from memory
    const sensitiveElements = document.querySelectorAll('[data-sensitive="true"]');
    sensitiveElements.forEach(el => {
      el.textContent = '';
      el.value = '';
    });
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log security event
   */
  async logSecurityEvent(eventType, additionalData = {}) {
    try {
      await fetch('/api/security/audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          type: eventType,
          sessionId: this.sessionId,
          timestamp: new Date().toISOString(),
          ...additionalData
        })
      });
    } catch (error) {
      console.error('[Session Security] Failed to log event:', error);
    }
  }

  /**
   * Get session info
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      startTime: sessionStorage.getItem('session_start'),
      lastActivity: this.lastActivity,
      isLocked: this.isLocked,
      duration: Date.now() - sessionStorage.getItem('session_start')
    };
  }

  /**
   * Cleanup
   */
  cleanup() {
    clearTimeout(this.inactivityTimer);
    clearInterval(this.sessionCheckInterval);
    console.info('[Session Security] Cleaned up');
  }
}

// Add fade in animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
document.head.appendChild(style);

const sessionSecurity = new SessionSecurityManager();
export default sessionSecurity;
